<template>
  <div class="vf">
    <header class="vf-band">
      <div class="vf-band__inner">
        <div class="vf-band__bar">
          <div class="esign-logo" role="img" aria-label="e-Sign Universitas Indonesia">
            <span class="esign-logo__emblem"><img src="../assets/logo-ui.png" alt=""></span>
            <span class="esign-logo__divider" aria-hidden="true"></span>
            <span class="esign-logo__text" aria-hidden="true">
              <span class="esign-logo__app">E-Sign</span>
              <span class="esign-logo__uni">Universitas</span>
              <span class="esign-logo__uni">Indonesia</span>
            </span>
          </div>
          <button type="button" class="vf-home" @click="toHome()">
            <v-icon small>mdi-arrow-left</v-icon> Beranda
          </button>
        </div>

        <h1 class="vf-title">Verifikasi dokumen</h1>
        <p class="vf-lead">
          Periksa keaslian tanda tangan elektronik pada dokumen PDF yang diterbitkan
          melalui e-Sign Universitas Indonesia.
        </p>
      </div>
    </header>

    <main class="vf-body">
      <section class="vf-card" aria-live="polite">
        <!-- Opened from a QR code: the document is checked automatically -->
        <div v-if="byId && isLoading" class="vf-loading">
          <v-progress-circular indeterminate color="primary" size="36" width="3" />
          <p>Memeriksa tanda tangan dokumen…</p>
        </div>

        <!-- Upload -->
        <form v-else-if="!result" class="vf-upload" @submit.prevent="postData()">
          <label
            class="vf-drop"
            :class="{ 'vf-drop--over': dragOver, 'vf-drop--filled': file }"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,application/pdf"
              class="vf-drop__input"
              @change="onPick"
            >
            <template v-if="!file">
              <v-icon class="vf-drop__icon" size="40">mdi-file-upload-outline</v-icon>
              <span class="vf-drop__title">Tarik file PDF ke sini</span>
              <span class="vf-drop__hint">atau <u>pilih dari perangkat</u></span>
            </template>
            <template v-else>
              <v-icon class="vf-drop__icon" size="40">mdi-file-pdf-box</v-icon>
              <span class="vf-drop__title vf-drop__name">{{ file.name }}</span>
              <span class="vf-drop__hint">{{ fileSize }} · <u>ganti file</u></span>
            </template>
          </label>

          <p v-if="errorText" class="vf-error" role="alert">
            <v-icon small color="error">mdi-alert-circle</v-icon> {{ errorText }}
          </p>

          <v-btn
            type="submit"
            color="primary"
            block
            large
            depressed
            :disabled="!file"
            :loading="isLoading"
          >
            Periksa tanda tangan
          </v-btn>

          <p class="vf-note">
            File tidak disimpan. Pemeriksaan dilakukan oleh
            <img src="../assets/bsre.png" alt="Balai Sertifikasi Elektronik (BSrE)" class="vf-bsre">
          </p>
        </form>

        <!-- Result -->
        <div v-else class="vf-result">
          <div class="vf-verdict" :class="result.valid ? 'vf-verdict--valid' : 'vf-verdict--invalid'">
            <v-icon class="vf-verdict__icon" size="52">
              {{ result.valid ? 'mdi-check-decagram' : 'mdi-alert-decagram' }}
            </v-icon>
            <div>
              <h2 class="vf-verdict__title">
                {{ result.valid ? 'Tanda tangan valid' : 'Tanda tangan tidak valid' }}
              </h2>
              <p class="vf-verdict__text">
                {{ result.valid
                  ? 'Dokumen ini ditandatangani secara elektronik dan isinya tidak berubah sejak ditandatangani.'
                  : 'Tanda tangan pada dokumen ini tidak dapat dipastikan keasliannya, atau isi dokumen telah berubah.' }}
              </p>
              <p v-if="result.notes" class="vf-verdict__notes">Catatan BSrE: {{ result.notes }}</p>
            </div>
          </div>

          <dl class="vf-facts">
            <div>
              <dt>Nama file</dt>
              <dd class="vf-break">{{ result.fileName || '-' }}</dd>
            </div>
            <div v-if="result.nomorSurat">
              <dt>Nomor surat</dt>
              <dd>{{ result.nomorSurat }}</dd>
            </div>
            <div v-if="result.tanggal">
              <dt>Tanggal pengajuan</dt>
              <dd>{{ result.tanggal }}</dd>
            </div>
            <div>
              <dt>Jumlah tanda tangan</dt>
              <dd>{{ result.jumlah }}</dd>
            </div>
          </dl>

          <template v-if="result.details.length">
            <h3 class="vf-subtitle">Penandatangan</h3>
            <ol class="vf-signers">
              <li v-for="(d, i) in result.details" :key="i" class="vf-signer">
                <p class="vf-signer__name">{{ signerName(d) }}</p>
                <p v-if="d.info_signer && d.info_signer.issuer_dn" class="vf-signer__issuer vf-break">
                  Diterbitkan oleh {{ issuerName(d.info_signer.issuer_dn) }}
                </p>
                <dl class="vf-signer__meta">
                  <div v-if="d.signature_document && d.signature_document.signed_in">
                    <dt>Waktu tanda tangan</dt>
                    <dd>{{ formatSignedIn(d.signature_document.signed_in) }}</dd>
                  </div>
                  <div v-if="d.info_signer && d.info_signer.signer_cert_validity">
                    <dt>Sertifikat</dt>
                    <dd>
                      <esign-status-tag
                        :tone="isValidText(d.info_signer.signer_cert_validity) ? 'success' : 'danger'"
                        :value="certText(d.info_signer.signer_cert_validity)"
                      />
                    </dd>
                  </div>
                  <div v-if="d.signature_document">
                    <dt>Isi dokumen</dt>
                    <dd>
                      <esign-status-tag
                        :tone="d.signature_document.document_integrity ? 'success' : 'danger'"
                        :value="d.signature_document.document_integrity ? 'Tidak berubah' : 'Berubah'"
                      />
                    </dd>
                  </div>
                  <div v-if="d.info_tsa && d.info_tsa.name">
                    <dt>Cap waktu</dt>
                    <dd class="vf-break">{{ d.info_tsa.name }}</dd>
                  </div>
                </dl>
              </li>
            </ol>
          </template>

          <div class="vf-actions">
            <v-btn v-if="result.url" :href="result.url" target="_blank" rel="noopener" color="primary" depressed>
              <v-icon left small>mdi-open-in-new</v-icon> Lihat detail dokumen
            </v-btn>
            <v-btn outlined color="primary" @click="reset()">Periksa file lain</v-btn>
          </div>
        </div>
      </section>

      <p v-if="byId && errorText && !result" class="vf-error vf-error--page" role="alert">
        <v-icon small color="error">mdi-alert-circle</v-icon> {{ errorText }}
      </p>
    </main>
  </div>
</template>

<script>
import { apiServer } from "../setup-axios";
import moment from "moment";

export default {
  props: {
    id_arsip_dokumen: null,
    tipe: ""
  },
  data() {
    return {
      isLoading: false,
      file: null,
      dragOver: false,
      errorText: "",
      // filled after a check; shown in place of the upload form
      result: null,
    };
  },
  computed: {
    byId() {
      return !!(this.id_arsip_dokumen && this.tipe)
    },
    fileSize() {
      if (!this.file) return ""
      const kb = this.file.size / 1024
      return kb < 1024 ? Math.max(1, Math.round(kb)) + " KB" : (kb / 1024).toFixed(1).replace(".", ",") + " MB"
    },
  },
  created() {
    if (this.byId) {
      this.verifyFileById()
    }
  },
  methods: {
    toHome() {
      this.$router.push("/home-page")
    },
    setFile(f) {
      this.errorText = ""
      if (!f) return
      if (!/\.pdf$/i.test(f.name) && f.type !== "application/pdf") {
        this.file = null
        this.errorText = "File harus berformat PDF."
        return
      }
      this.file = f
    },
    onPick(e) {
      this.setFile(e.target.files[0])
      e.target.value = ""
    },
    onDrop(e) {
      this.dragOver = false
      this.setFile(e.dataTransfer.files[0])
    },
    reset() {
      this.result = null
      this.file = null
      this.errorText = ""
      if (this.byId) this.$router.replace("/verify-file")
    },
    toResult(verify, extra) {
      return Object.assign({
        valid: verify.summary == "VALID",
        fileName: verify.nama_dokumen,
        notes: verify.notes,
        jumlah: verify.jumlah_signature,
        details: verify.details || [],
        nomorSurat: "",
        tanggal: "",
        url: "",
      }, extra)
    },
    postData() {
      if (!this.file) {
        this.errorText = "Pilih file PDF terlebih dahulu."
        return
      }
      this.isLoading = true
      this.errorText = ""
      const form = new FormData();
      form.append('signed_file', this.file);
      apiServer
        .post("/verify_file", form)
        .then(response => {
          this.isLoading = false;
          if (response.data.status == 'ok') {
            this.result = this.toResult(response.data.data)
          } else {
            this.errorText = response.data.message || "File tidak dapat diperiksa."
          }
        })
        .catch(error => {
          console.log("There was an error:", error);
          this.isLoading = false;
          this.errorText = this.errorMessage(error)
        });
    },
    verifyFileById() {
      this.isLoading = true
      apiServer
        .post("/verify_file_by_id", {
          id: Number(this.id_arsip_dokumen),
          tipe: this.tipe
        })
        .then(response => {
          this.isLoading = false;
          if (response.data.status == 'ok') {
            const data = response.data.data
            this.result = this.toResult(data.verify_bsre_response, {
              nomorSurat: data.nomor_surat,
              tanggal: data.tanggal_pengajuan ? moment.utc(data.tanggal_pengajuan).locale("id").format("DD-MM-YYYY HH:mm:ss") : "",
              url: data.url ? "http://" + data.url : "",
            })
          } else {
            this.errorText = response.data.message || "Dokumen tidak dapat diperiksa."
          }
        })
        .catch(error => {
          console.log("There was an error:", error);
          this.isLoading = false;
          this.errorText = this.errorMessage(error)
        });
    },
    errorMessage(error) {
      return (error.response && error.response.data && error.response.data.message) || error.message
    },
    signerName(d) {
      return (d.info_signer && d.info_signer.signer_name) || "Tanpa nama"
    },
    // "CN=OSD LU Kelas 2, O=Badan Siber dan Sandi Negara, C=ID" -> "OSD LU Kelas 2"
    issuerName(dn) {
      const cn = /CN=([^,]+)/i.exec(dn)
      return cn ? cn[1].trim() : dn
    },
    isValidText(v) {
      return /valid/i.test(v) && !/tidak|invalid|not|expired/i.test(v)
    },
    certText(v) {
      return this.isValidText(v) ? "Valid" : v
    },
    formatSignedIn(v) {
      const m = moment(v)
      return m.isValid() ? m.locale("id").format("DD-MM-YYYY HH:mm:ss") : v
    },
  },
};
</script>

<style>
@import url("../css/drawer.css");
</style>

<style scoped>
.vf {
  min-height: 100vh;
  background: var(--esign-bg);
  color: var(--esign-text);
}

/* Navy band, same gradient as the app's top bar.
   style.css forces h1-h4 and p sizes with !important, so the text rules below do too */
.vf-band {
  background: var(--color-brand, #002B81);
  color: #fff;
  padding: 18px 16px 112px;
}

.vf-band__inner {
  max-width: 680px;
  margin: 0 auto;
}

.vf-band__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 40px;
}

.vf-home {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  transition: background-color var(--esign-fast) var(--esign-ease);
}

.vf-home .v-icon {
  color: inherit;
}

.vf-home:hover {
  background: rgba(255, 255, 255, 0.12);
}

.vf-home:focus-visible {
  outline: 2px solid var(--esign-accent);
  outline-offset: 2px;
}

.vf-title {
  margin: 0 0 8px !important;
  font-size: 30px !important;
  font-weight: 800 !important;
  line-height: 1.2 !important;
  letter-spacing: -0.01em;
  color: #fff;
}

.vf-lead {
  max-width: 54ch;
  margin: 0 !important;
  font-size: 15px !important;
  line-height: 1.6 !important;
  color: rgba(255, 255, 255, 0.85);
}

/* Card overlaps the band */
.vf-body {
  max-width: 712px;
  margin: -84px auto 0;
  padding: 0 16px 48px;
}

.vf-card {
  background: var(--esign-surface);
  border: 1px solid var(--esign-border);
  border-radius: var(--esign-radius-lg);
  box-shadow: var(--esign-shadow-md);
  padding: 24px;
}

/* Upload */
.vf-drop {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 36px 16px;
  margin-bottom: 16px;
  border: 2px dashed var(--esign-border-strong);
  border-radius: var(--esign-radius-md);
  background: var(--esign-surface-muted);
  text-align: center;
  cursor: pointer;
  transition: border-color var(--esign-fast) var(--esign-ease), background-color var(--esign-fast) var(--esign-ease);
}

.vf-drop:hover,
.vf-drop--over {
  border-color: var(--esign-primary);
  background: var(--esign-primary-soft);
}

.vf-drop:focus-within {
  outline: 2px solid var(--esign-primary);
  outline-offset: 2px;
}

.vf-drop--filled {
  border-style: solid;
  border-color: var(--esign-primary);
  background: var(--esign-primary-soft);
}

.vf-drop__input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.vf-drop__icon.v-icon {
  margin-bottom: 6px;
  color: var(--esign-primary);
}

.vf-drop__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--esign-text);
}

.vf-drop__name {
  max-width: 100%;
  overflow-wrap: anywhere;
}

.vf-drop__hint {
  font-size: 13px;
  color: var(--esign-text-muted);
}

.vf-drop__hint u {
  color: var(--esign-primary-strong);
  text-underline-offset: 2px;
}

.vf-error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: -4px 0 14px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: var(--esign-error);
}

.vf-error--page {
  justify-content: center;
  margin-top: 16px;
}

.vf-note {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0 0 !important;
  font-size: 12px !important;
  color: var(--esign-text-subtle);
}

.vf-bsre {
  height: 26px;
  width: auto;
}

.vf-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 32px 0;
  color: var(--esign-text-muted);
}

.vf-loading p {
  margin: 0 !important;
  font-size: 14px !important;
}

/* Result: the verdict is the one loud element on the page */
.vf-verdict {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: var(--esign-radius-md);
  border: 1px solid;
}

.vf-verdict--valid {
  background: var(--esign-success-bg);
  border-color: rgba(21, 128, 61, 0.25);
}

.vf-verdict--invalid {
  background: var(--esign-error-bg);
  border-color: rgba(220, 38, 38, 0.25);
}

.vf-verdict__icon.v-icon {
  flex: 0 0 auto;
}

.vf-verdict--valid .vf-verdict__icon.v-icon,
.vf-verdict--valid .vf-verdict__title {
  color: var(--esign-success);
}

.vf-verdict--invalid .vf-verdict__icon.v-icon,
.vf-verdict--invalid .vf-verdict__title {
  color: var(--esign-error);
}

.vf-verdict__title {
  margin: 4px 0 6px !important;
  font-size: 22px !important;
  font-weight: 800 !important;
  line-height: 1.25 !important;
}

.vf-verdict__text,
.vf-verdict__notes {
  margin: 0 !important;
  font-size: 14px !important;
  line-height: 1.55 !important;
  color: var(--esign-text);
}

.vf-verdict__notes {
  margin-top: 8px;
  color: var(--esign-text-muted);
}

.vf-facts,
.vf-signer__meta {
  margin: 0;
}

.vf-facts > div,
.vf-signer__meta > div {
  display: grid;
  grid-template-columns: 38% 1fr;
  gap: 12px;
  align-items: center;
}

.vf-facts > div {
  padding: 9px 0;
  border-bottom: 1px solid var(--esign-border);
}

.vf-facts dt,
.vf-signer__meta dt {
  font-size: 13px;
  font-weight: 600;
  color: var(--esign-text-muted);
}

.vf-facts dd,
.vf-signer__meta dd {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
}

.vf-break {
  overflow-wrap: anywhere;
}

.vf-subtitle {
  margin: 24px 0 10px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
}

.vf-signers {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;
}

.vf-signer {
  padding: 14px 16px;
  border: 1px solid var(--esign-border);
  border-radius: var(--esign-radius-sm);
}

.vf-signer__name {
  margin: 0 !important;
  font-size: 15px !important;
  font-weight: 700 !important;
}

.vf-signer__issuer {
  margin: 2px 0 0 !important;
  font-size: 12px !important;
  color: var(--esign-text-subtle);
}

.vf-signer__meta {
  margin-top: 10px;
}

.vf-signer__meta > div {
  padding: 4px 0;
}

.vf-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

@media (max-width: 600px) {
  .vf-band {
    padding-bottom: 104px;
  }

  .vf-band__bar {
    margin-bottom: 28px;
  }

  .vf-title {
    font-size: 24px;
  }

  .vf-card {
    padding: 18px 16px;
  }

  .vf-verdict {
    flex-direction: column;
    gap: 8px;
  }

  .vf-facts > div,
  .vf-signer__meta > div {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  .vf-actions .v-btn {
    flex: 1 1 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .vf-drop,
  .vf-home {
    transition: none;
  }
}
</style>
